import { PROVIDER_ACCOUNT_NAMES, type ProviderKind } from "@cmmd-forge/contracts";

import type {
  CmmdClaudeCredentialState,
  CmmdCodexCredentialState,
} from "../../environments/cmmdApi";
import type { CloudProviderSignInController } from "../../hooks/useCloudProviderSignIn";
import {
  ActiveSignInPhase,
  CredentialNeedsReconnectView,
  SettledProviderSignInView,
  StatusLine,
} from "./CloudProviderSignInViews";

type CredentialState = CmmdCodexCredentialState | CmmdClaudeCredentialState;

/**
 * Cloud-only personal-provider connection surface. Its controller owns the
 * durable CMMD credential state; this component only selects the truthful
 * connected, reconnect, in-flight, or disconnected renderer.
 */
export function CloudProviderSignInView({
  controller,
  provider,
  onCancel,
  connectedMode = "standalone",
}: {
  readonly controller: CloudProviderSignInController<CredentialState>;
  readonly provider: ProviderKind;
  readonly onCancel?: (() => void) | undefined;
  readonly connectedMode?: "manage" | "standalone";
}) {
  const showConnected =
    controller.connection?.connected === true &&
    !credentialNeedsReconnect(controller.connection) &&
    !controller.busy &&
    controller.phase?.kind !== "awaiting_user" &&
    controller.phase?.kind !== "error";

  return (
    <div className="mt-3 rounded-md border border-border/70 bg-muted/20 p-3 text-xs">
      {showConnected ? (
        <div className="font-medium text-foreground">
          Personal {PROVIDER_ACCOUNT_NAMES[provider]} subscription
        </div>
      ) : (
        <>
          <div className="font-medium text-foreground">
            Connect {PROVIDER_ACCOUNT_NAMES[provider]} securely
          </div>
          <div className="mt-0.5 text-muted-foreground">
            Forge uses a temporary sign-in runtime, saves the credential to your account, then
            destroys that runtime. Nothing is stored on this device.
          </div>
        </>
      )}
      <SignInBody
        controller={controller}
        provider={provider}
        showConnected={showConnected}
        onCancel={onCancel}
        connectedMode={connectedMode}
      />
    </div>
  );
}

function SignInBody({
  controller,
  provider,
  showConnected,
  onCancel,
  connectedMode,
}: {
  readonly controller: CloudProviderSignInController<CredentialState>;
  readonly provider: ProviderKind;
  readonly showConnected: boolean;
  readonly onCancel?: (() => void) | undefined;
  readonly connectedMode: "manage" | "standalone";
}) {
  const { phase, busy, connection, disconnecting, start, cancel, disconnect } = controller;

  if (isActiveSignInPhase(phase)) {
    return (
      <ActiveSignInPhase
        phase={phase}
        provider={provider}
        onStart={start}
        onCancel={() => {
          cancel();
          onCancel?.();
        }}
      />
    );
  }
  if (busy) {
    return <StatusLine label={busyLabel(phase?.kind)} />;
  }
  if (isCredentialReconnectRequired(connection)) {
    return (
      <CredentialNeedsReconnectView
        provider={provider}
        disconnecting={disconnecting}
        onReconnect={() => start(provider)}
        onDisconnect={disconnect}
      />
    );
  }
  return (
    <SettledProviderSignInView
      controller={controller}
      provider={provider}
      showConnected={showConnected}
      connectedMode={connectedMode}
    />
  );
}

function isActiveSignInPhase(
  phase: CloudProviderSignInController["phase"],
): phase is Extract<
  NonNullable<CloudProviderSignInController["phase"]>,
  { kind: "awaiting_user" | "error" }
> {
  return phase?.kind === "awaiting_user" || phase?.kind === "error";
}

function isCredentialReconnectRequired(
  credential: CredentialState | null,
): credential is Extract<CmmdCodexCredentialState, { connected: true }> {
  return credential?.connected === true && credentialNeedsReconnect(credential);
}

function credentialNeedsReconnect(credential: CredentialState): boolean {
  return "needsReconnect" in credential && credential.needsReconnect;
}

function busyLabel(kind: string | undefined): string {
  if (kind === "provisioning") return "Starting a secure sign-in runtime…";
  if (kind === "connecting") return "Connecting…";
  if (kind === "finishing") return "Saving your credential…";
  return "Working…";
}
